import { useState } from "react";
import * as R from "ramda";
import { arrayOf, bool, number, shape, string } from "prop-types";
import NarrativeChart from "../NarrativeChart/NarrativeChart.jsx";
import {
  getDataByLocation,
  indicatorDataShape,
} from "../../../../Common/js/common/datacenterData.js";
import useBoundingClientRect from "../../common/useBoundingClientRect.js";

const propTypes = {
  title: string.isRequired,
  shortDescription: string,
  source: string.isRequired,
  backgroundColor: string.isRequired,
  foregroundColor: string.isRequired,
  dottedLineForMissingData: bool,
  excludeUrl: string,
  primary: shape({
    indicator: number.isRequired,
    location: number.isRequired,
  }).isRequired,
  results: shape({
    data: arrayOf(indicatorDataShape).isRequired,
    errorMessage: string,
  }).isRequired,
};

const NarrativeHeaderChart = (props) => {
  const {
    backgroundColor,
    foregroundColor,
    dottedLineForMissingData,
    shortDescription,
    source,
    title,
    primary,
    excludeUrl,
  } = props;

  const { data } = props.results;

  const primaryIndicatorData = R.find(
    R.pathEq(["indicator", "id"], primary.indicator),
    data,
  );

  const dataByLocation = getDataByLocation(primaryIndicatorData);
  const primaryData = dataByLocation[primary.location];

  // start with the last year selected
  const lastYear = R.compose(
    R.prop("timeFrame"),
    R.last,
    R.prop("dataPoints"),
  )(primaryData);

  const [year, setYear] = useState(lastYear);
  const [containerDimensions, containerRef] = useBoundingClientRect();

  // the other lines will always be the other locations in the primary data
  const otherLinesData = R.compose(
    R.filter((data) => data.dataPoints && data.dataPoints.length), // filter out locations with no data
    R.map((location) => dataByLocation[location.id]),
    R.reject(R.propEq("id", primary.location)),
    R.prop("locations"),
  )(primaryIndicatorData);

  const containerWidth = R.propOr(400, "width", containerDimensions);

  const chartWidth = Math.min(400, containerWidth); // don't overflow small screens
  const chartStyle = {
    width: `${chartWidth}px`,
  };

  return (
    <div
      className="narrative-header-chart"
      ref={containerRef}
      style={{
        backgroundColor,
        color: foregroundColor,
        "--background-color": backgroundColor,
        "--foreground-color": foregroundColor,
        overflow: "hidden", // so the svg doesn't grow the container
      }}
    >
      <div className="container container--100">
        <div className="narrative-header-chart__title">{title}</div>
        {shortDescription && (
          <div className="narrative-header-chart__short-description">
            {shortDescription}
          </div>
        )}

        <div
          className="container__col container__col--flex narrative-header-chart__chart"
          style={chartStyle}
        >
          <NarrativeChart
            width={chartWidth}
            height={400}
            dottedLineForMissingData={dottedLineForMissingData}
            primaryData={primaryData}
            otherLinesData={otherLinesData}
            hoveredYear={year}
            onYearHover={setYear}
            showLineLabels={true}
            showValueOnHover={true}
            excludeUrl={excludeUrl}
          />

          {source && (
            <div className="narrative-header-chart__source">{source}</div>
          )}
        </div>
      </div>
    </div>
  );
};

NarrativeHeaderChart.propTypes = propTypes;
export default NarrativeHeaderChart;
